import type { UniversalModifierValue } from "schemas/generated/fileModifiers";

import React, { createContext } from "react";

import { getModifierKey } from "@v2/features/modifiers/utils";
import { DefaultMap } from "replo-utils/lib/map";
import { useRequiredContext } from "replo-utils/react/context";
import { useShallowStore } from "replo-utils/react/zustand";
import { createStore } from "zustand";

export interface ModifierApplyAction {
  modifier: UniversalModifierValue;
  stableElementId: string;
}

export interface ModifierRequest {
  projectId: string;
  pageId: string;
  actions: ModifierApplyAction[];
  timestamp: number;
}

interface ModifierQueueStore {
  queue: ModifierRequest[];
  isProcessing: boolean;
  lastProcessedRequest: ModifierRequest | null;
  waitingForCleanup: boolean;

  actions: {
    enqueue: (request: ModifierRequest) => void;
    dequeue: () => void;
    setIsProcessing: (isProcessing: boolean) => void;
    setLastProcessedRequest: (request: ModifierRequest | null) => void;
    setWaitingForCleanup: (waiting: boolean) => void;
    clearQueue: () => void;
  };
}

/**
 * Collapses modifier actions by keeping only the latest value for each
 * (stableElementId, modifier.type) combination. Once a delete modifier is seen
 * for a stableElementId, all other modifiers for that stableElementId are ignored.
 */
export const collapseActions = (
  actions: ModifierApplyAction[],
): ModifierApplyAction[] => {
  const stableElementIdMap = new DefaultMap<
    string,
    { isDeleted: boolean; modifiers: Map<string, ModifierApplyAction> }
  >(() => ({ isDeleted: false, modifiers: new Map() }));

  for (const action of actions) {
    const { stableElementId, modifier } = action;
    const entry = stableElementIdMap.get(stableElementId);

    if (entry.isDeleted) {
      continue;
    }

    if (modifier.type === "delete") {
      entry.isDeleted = true;
      entry.modifiers.clear();
      entry.modifiers.set("delete", action);
    } else {
      const key = getModifierKey(modifier);
      entry.modifiers.set(key, action);
    }
  }

  const result: ModifierApplyAction[] = [];
  for (const entry of stableElementIdMap.values()) {
    result.push(...entry.modifiers.values());
  }

  return result;
};

/**
 * Get optimistic value for a specific (stableElementId, modifier type) from the queue.
 * Returns the most recent modifier value if found, null otherwise.
 *
 * NOTE (Kurt, 2025-11-09): Also checks lastProcessedRequest if isProcessing is true.
 * This prevents the UI from flickering to stale values after dequeue but before
 * selectedElement is updated with fresh data from the server.
 */
export function getOptimisticValue({
  queue,
  lastProcessedRequest,
  stableElementId,
  modifierType,
}: {
  queue: ModifierRequest[];
  lastProcessedRequest: ModifierRequest | null;
  isProcessing: boolean;
  stableElementId: string;
  modifierType: UniversalModifierValue;
}): UniversalModifierValue | null {
  const modifierKey = getModifierKey(modifierType);

  for (let i = queue.length - 1; i >= 0; i--) {
    const request = queue[i];
    if (!request) {
      continue;
    }

    const action = request.actions.find((action) => {
      return (
        action.stableElementId === stableElementId &&
        getModifierKey(action.modifier) === modifierKey
      );
    });

    if (action) {
      return action.modifier;
    }
  }

  // Note (Kevin, 2025-12-03): Always check lastProcessedRequest as a fallback,
  // not just when queue is empty. This handles the case where Modifier A is
  // processed (stored in lastProcessedRequest), then Modifier B is enqueued.
  // Without this, looking up Modifier A would fail since queue only has B.
  if (lastProcessedRequest) {
    const action = lastProcessedRequest.actions.find((action) => {
      return (
        action.stableElementId === stableElementId &&
        getModifierKey(action.modifier) === modifierKey
      );
    });

    if (action) {
      return action.modifier;
    }
  }

  return null;
}

export const createModifierQueueStore = () => {
  return createStore<ModifierQueueStore>((set, get) => ({
    queue: [],
    isProcessing: false,
    lastProcessedRequest: null,
    waitingForCleanup: false,

    actions: {
      /**
       * NOTE (Kurt, 2025-10-31): Enqueue modifier requests and merge rapid
       * actions on the same page. Example flow:
       *
       * 1. User drags slider on Page A → queue = [{pageA, action1}]
       * 2. User keeps dragging → last item is Page A, so merge
       *    → queue = [{pageA, actions: [action1, action2]}]
       * 3. User switches to Page B, selects element, changes color → B !== A
       *    → queue = [{pageA, actions: [...]}, {pageB, action1}]
       * 4. Processing finishes A, dequeues it → queue = [{pageB, action1}]
       * 5. Queue continues with B
       *
       * We do this to make sure stable element IDs from one page don't get applied to
       * a different page.
       */
      enqueue: (request: ModifierRequest) => {
        const { queue, isProcessing } = get();
        const existingRequest = queue[queue.length - 1];

        // NOTE (Kurt, 2025-11-04): If the last request has a delete action,
        // we don't want to merge the new request with the last request. Create
        // a new request instead.
        const hasDeleteAction = existingRequest?.actions.some(
          (action) => action.modifier.type === "delete",
        );

        // NOTE (Kurt, 2025-11-06): Don't merge with a request that's currently
        // being processed. If queue.length === 1 and isProcessing === true, the
        // last item is the one being sent to the server right now. Merging with
        // it would cause a race condition where the server responds with the old
        // value, but the queue has the new merged value.
        const isLastItemBeingProcessed = queue.length === 1 && isProcessing;

        if (
          existingRequest &&
          existingRequest.projectId === request.projectId &&
          existingRequest.pageId === request.pageId &&
          !hasDeleteAction &&
          !isLastItemBeingProcessed
        ) {
          const mergedActions = collapseActions([
            ...existingRequest.actions,
            ...request.actions,
          ]);

          set({
            queue: [
              ...queue.slice(0, -1),
              {
                ...existingRequest,
                actions: mergedActions,
              },
            ],
          });
        } else {
          set({
            queue: [
              ...queue,
              { ...request, actions: collapseActions(request.actions) },
            ],
          });
        }
      },

      dequeue: () => {
        const { queue } = get();

        if (queue.length === 0) {
          console.warn("[ModifierQueue] Attempted to dequeue from empty queue");
          return;
        }

        set({
          queue: queue.slice(1),
        });
      },

      setIsProcessing: (isProcessing: boolean) => {
        set({ isProcessing });
      },

      setLastProcessedRequest: (request: ModifierRequest | null) => {
        set({ lastProcessedRequest: request });
      },

      setWaitingForCleanup: (waiting: boolean) => {
        set({ waitingForCleanup: waiting });
      },

      clearQueue: () => {
        set({
          queue: [],
          isProcessing: false,
          lastProcessedRequest: null,
          waitingForCleanup: false,
        });
      },
    },
  }));
};

type ModifierQueueContextType = {
  store: ReturnType<typeof createModifierQueueStore>;
};

const ModifierQueueStoreContext =
  createContext<ModifierQueueContextType | null>(null);

export const useModifierQueueContext = () => {
  return useRequiredContext(ModifierQueueStoreContext);
};

export const ModifierQueueProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [store] = React.useState(() => createModifierQueueStore());

  const contextValue = React.useMemo(
    () => ({
      store,
    }),
    [store],
  );

  return (
    <ModifierQueueStoreContext.Provider value={contextValue}>
      {children}
    </ModifierQueueStoreContext.Provider>
  );
};

function useModifierQueueStore<T = ModifierQueueStore>(
  selector: (state: ModifierQueueStore) => T = (state) => state as unknown as T,
): T {
  const { store } = useModifierQueueContext();
  return useShallowStore(store, selector);
}

export function useQueue() {
  return useModifierQueueStore((state) => state.queue);
}

export function useIsProcessing() {
  return useModifierQueueStore((state) => state.isProcessing);
}

export function useLastProcessedRequest() {
  return useModifierQueueStore((state) => state.lastProcessedRequest);
}

export function useWaitingForCleanup() {
  return useModifierQueueStore((state) => state.waitingForCleanup);
}

export function useModifierQueueActions() {
  return useModifierQueueStore((state) => state.actions);
}
